import { useCallback, useEffect, useRef, useState } from 'react';

import { noop, type IconName } from '@garnish/core';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeElements, type ConfirmationToken, type StripeError } from '@stripe/stripe-js';
import {
  type EmbeddedPaymentElementResult,
  type IntentCreationCallbackParams,
} from '@stripe/stripe-react-native';

import { StripeLogger } from '../../utils';
import { getSelectedPaymentMethod } from './helpers';
import { useLabelAndIconByType, usePaymentMethodChangeHandler } from './hooks';

import type { UseStripePaymentProps } from '../../types';

export const useStripePaymentElement = (props: UseStripePaymentProps) => {
  const { paymentMethods, stripeIntentConfigRef } = props;

  // ─── Stripe ────────────────────────────────────────────────────────────────

  const stripe = useStripe();
  const elements = useElements();

  // ─── State ─────────────────────────────────────────────────────────────────

  const isPaymentAlreadySavedRef = useRef(false);
  const selectedPaymentMethodIdRef = useRef('');
  const [isAddingNewPaymentMethod, setIsAddingNewPaymentMethod] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState('');
  const [selectedPaymentIcon, setSelectedPaymentIcon] = useState<IconName>('IconCreditCard'); // prettier-ignore

  // ─── Derived ───────────────────────────────────────────────────────────────

  const { isApplePay, isGooglePay, isLink, isCashApp } = getSelectedPaymentMethod(selectedPaymentMethodType); // prettier-ignore
  const isPaymentSetupComplete = isAddingNewPaymentMethod && Boolean(selectedPaymentMethod);

  // ─── Payment Method Preview ────────────────────────────────────────────────

  const createConfirmationToken = useCallback(
    async (shouldSubmit = false) => {
      if (!stripe || !elements) {
        throw new Error('Stripe not initialized');
      }

      if (shouldSubmit) {
        const { error: submitError } = await elements.submit();

        if (submitError) {
          StripeLogger.logSubmitError(submitError);
          throw new Error(submitError.message);
        }
      }

      const { error, confirmationToken } = await stripe.createConfirmationToken({
        elements,
      });

      if (error) {
        StripeLogger.logCreatePaymentMethodError(error);
        throw new Error(error.message);
      }

      return confirmationToken;
    },
    [elements, stripe],
  );

  // ─── Handle Payment Change ─────────────────────────────────────────────────

  const convertTypeToLabelAndIcon = useLabelAndIconByType();
  const handlePaymentElementChange = usePaymentMethodChangeHandler({
    isPaymentAlreadySavedRef,
    selectedPaymentMethodIdRef,
    paymentMethods,
    convertTypeToLabelAndIcon,
    setIsAddingNewPaymentMethod,
    setSelectedPaymentMethod,
    setSelectedPaymentMethodType,
    setSelectedPaymentIcon,
    createConfirmationToken,
  });

  useEffect(() => {
    if (!stripe || !elements) return;

    const paymentElement = elements.getElement('payment');

    if (!paymentElement) return;

    paymentElement.on('change', handlePaymentElementChange);

    return () => {
      paymentElement.off('change', handlePaymentElementChange);
    };
  }, [elements, stripe, handlePaymentElementChange]);

  // ─── Confirm Callback ──────────────────────────────────────────────────────

  const confirmStripe = useCallback(async (): Promise<ConfirmResult> => {
    try {
      if (!stripe || !elements) {
        StripeLogger.logStripeNotInitializedError();
        return toFailure('Stripe not initialized');
      }

      const { error: submitError, selectedPaymentMethod } = await elements.submit();

      if (submitError) {
        StripeLogger.logSubmitError(submitError);
        return toFailure(submitError.message);
      }

      const confirmHandler = stripeIntentConfigRef?.current?.confirmHandler;

      if (!confirmHandler) {
        return toFailure('Stripe confirm handler is not available');
      }

      // Maps the stripe-react-native interface to web.
      return await new Promise<ConfirmResult>((resolve) => {
        const confirmIntent = async (
          params: IntentCreationCallbackParams,
        ): Promise<ConfirmResult> => {
          if (!params.clientSecret) {
            return toFailure(params.error?.message ?? 'No client secret');
          }

          const configForNewPaymentMethods = await getConfigForNewPaymentMethods(
            isPaymentAlreadySavedRef.current,
            elements,
            createConfirmationToken,
          );

          const confirmResult = await stripe.confirmPayment({
            clientSecret: params.clientSecret,
            redirect: redirectCondition,
            confirmParams: { return_url: defaultRedirect },
            ...configForNewPaymentMethods,
          });

          if (confirmResult?.error) {
            return toFailure(confirmResult.error.message, confirmResult.error.code);
          }

          if (confirmResult?.paymentIntent?.status === 'requires_action') {
            return { status: 'canceled' };
          }

          return { status: 'completed' };
        };

        const resolveWithError = (error: unknown) => {
          StripeLogger.logGenericError(toError(error));
          resolve(toFailure(error));
        };

        // `confirmHandler` is async, so its rejection has to be routed back to this promise.
        void Promise.resolve(
          confirmHandler(
            {
              id: selectedPaymentMethodIdRef.current,
              paymentMethodType: selectedPaymentMethod, // used for telemetry only.
            } as never,
            false,
            (params) => {
              void confirmIntent(params).then(resolve, resolveWithError);
            },
          ),
        ).catch(resolveWithError);
      });
    } catch (error) {
      StripeLogger.logGenericError(toError(error));
      return toFailure(error);
    }
  }, [stripe, elements, stripeIntentConfigRef, createConfirmationToken]);

  return {
    embeddedPaymentElementView: undefined, // component rendering on web
    isUpdatingTotal: false, // atomic operation on web
    isGooglePay,
    isApplePay,
    isLink,
    isCashApp,
    isAddingNewPaymentMethod,
    isPaymentSetupComplete,
    selectedPaymentMethodType,
    selectedPaymentMethod,
    selectedPaymentImage: undefined, // svg icon on web
    selectedPaymentIcon,
    confirmStripe,
    updateStripe: noop,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function getConfigForNewPaymentMethods(
  isPaymentAlreadySaved: boolean,
  elements: StripeElements,
  createConfirmationToken: () => Promise<ConfirmationToken>,
) {
  if (isPaymentAlreadySaved) return { elements };

  const confirmationToken = await createConfirmationToken();

  if (!confirmationToken) return { elements };

  return {
    confirmParams: {
      confirmation_token: confirmationToken?.id,
      return_url: defaultRedirect,
    },
  };
}

/**
 * `stripe-react-native` reports failures as an `Error`, but Stripe.js messages are optional
 * and thrown values are unknown, so everything is normalized to an `Error` at this boundary.
 */
function toError(value: unknown) {
  if (value instanceof Error) {
    return value;
  }

  return new Error(typeof value === 'string' && value ? value : GENERIC_ERROR_MESSAGE);
}

function toFailure(error: unknown, code?: StripeError['code']): ConfirmResult {
  return { status: 'failed', error: toError(error), code };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const redirectCondition = 'if_required' as const;
const defaultRedirect = window.location.origin;
const GENERIC_ERROR_MESSAGE = 'Something went wrong confirming the payment';

// ─── Types ───────────────────────────────────────────────────────────────────

type ConfirmResult = EmbeddedPaymentElementResult & { code?: StripeError['code'] };
